import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';

import { ErrorState, LoadingState } from '@/components/admin';
import { useClient } from '@/features/clients/hooks';
import { CaseReportEditor } from '@/features/sessions/components/CaseReportEditor';
import { useCaseReports, useSession } from '@/features/sessions/hooks';
import { requirePermission } from '@/lib/guards';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute(
  '/_staff/admin/workspace/sessions/$sessionId/case-reports/new',
)({
  beforeLoad: () => {
    requirePermission('case_report:create');
  },
  component: NewCaseReportScreen,
  // Editor page owns the full canvas — no shell padding around the card.
  staticData: { fullBleed: true },
});

export function NewCaseReportScreen() {
  const { sessionId } = useParams({
    from: '/_staff/admin/workspace/sessions/$sessionId/case-reports/new',
  });
  const navigate = useNavigate();
  const sessionQ = useSession(sessionId);
  const reportsQuery = useCaseReports(sessionId);
  const clientQ = useClient(sessionQ.data?.clientId ?? undefined);
  const userFirstName = useAuthStore((s) => s.user?.firstName ?? 'you');

  // If a non-approved report already exists, redirect to it instead of
  // creating a duplicate. The BE doesn't enforce uniqueness but the
  // UX assumption is one active report per session.
  const active = reportsQuery.data?.find((r) => r.status !== 'approved');

  useEffect(() => {
    if (active) {
      void navigate({
        to: '/admin/workspace/sessions/$sessionId/case-reports/$reportId',
        params: { sessionId, reportId: active.id },
        replace: true,
      });
    }
  }, [active, navigate, sessionId]);

  if (sessionQ.isLoading || reportsQuery.isLoading) return <LoadingState variant="form" />;
  if (sessionQ.error || reportsQuery.error) {
    const err = reportsQuery.error ?? sessionQ.error;
    return <ErrorState description={err instanceof Error ? err.message : undefined} />;
  }
  if (active) return null;
  if (sessionQ.data === undefined) return <p className="p-6">Session not found.</p>;

  const session = sessionQ.data;
  const client = clientQ.data;
  const clientName =
    client !== undefined
      ? `${client.firstName} ${client.lastName}`
      : (session.clientName ?? 'Client');

  // Full-bleed by spec — see sibling $reportId route for the rationale.
  return (
    <CaseReportEditor
      sessionId={sessionId}
      practitionerId={session.practitionerId}
      clientId={session.clientId}
      clientName={clientName}
      scheduledStart={session.scheduledStart}
      authorName={userFirstName}
      report={null}
      onCreated={(created) => {
        void navigate({
          to: '/admin/workspace/sessions/$sessionId/case-reports/$reportId',
          params: { sessionId, reportId: created.id },
          replace: true,
        });
      }}
      onSubmitted={() => undefined}
    />
  );
}
